import React, {
  useState, useEffect
} from 'react';
import {
  Container, Col, Row, Spinner,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { Edit24, Trash } from '@bphxd/ds-core-react/icons';
import ApolloClient from 'apollo-client';
import AWSAppSyncClient from 'aws-appsync';

import PropTypes from 'prop-types';
import { useAppContext } from '../../state/GlobalState';
import PageHeading from '../../components/PageHeading/PageHeading';
import withClient from '../../hoc/withClient';
import './_shipToMapping.scss';
import ConfirmActionModal from '../../components/ConfirmActionModal/ConfirmActionModal';
import NetworkSpeedChecker from '../../components/NetworkSpeedChecker/NetworkSpeedChecker';
import CommonReactTable from '../../components/CommonReactTable/CommonReactTable';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import CustomToast from '../../components/CustomToast/CustomToast';
import { deleteCarrierMapping, getCarrierMappings } from '../../api/graphQl/ShipToMapping';

const ShipToMappingPage = ({
  graphClient,
}) => {
  const history = useHistory();
  const {
    carrierId, carrierName, handleGraphApiError,
  } = useAppContext();

  const [shipToMappingsAll, setShipToMappingsAll] = useState([]);
  const [shipToNumber, setShipToNumber] = useState('');
  const [deleteShipData, setDeleteShipData] = useState({});
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    limit: 50,
    lastEvaluatedKey: null,
    currentPageData: [],
    prevPageData: [],
    nextPageData: [],
    isNext: false,
    isPrev: false,
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');
  const closeWindow = () => setShowConfirmDelete(false);

  const showToastMessage = (message, type) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const closeToast = () => setShowToast(false);

  const columns = [
    {
      accessorKey: 'ShipToNumber',
      header: 'Ship To Number',
      cell: ({ row }) => row.original?.ShipToNumber || 'N/A',
    },
    {
      accessorKey: 'ShipToName',
      header: 'Ship To Name',
      cell: ({ row }) => row.original?.ShipToName || 'N/A',
    },
    {
      accessorKey: 'SAPPlantId',
      header: 'SAP Plant ID',
      cell: ({ row }) => row.original?.SAPPlantId || 'N/A',
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const index = row.index;
        return (
          <div className="d-flex justify-content-center align-items-center">
            <div className="edit-icon-container">
              <Edit24 className="edit-icon" height="24" onClick={() => handleEdit(index)} />
            </div>
            <div className="trash-icon-container ms-3">
              {isDeleting && deleteIndex === index ? (
                <Spinner size="sm" />
              ) : (
                <Trash className="trash-icon" height="24"
                  onClick={() => handleDelete(index, row.original?.SAPPlantId, row.original?.ShipToNumber)}
                  data-testid="delete-btn"
                />
              )}
            </div>
          </div>
        );
      },
    },
  ];

  const fetchAndSetAllShipToMappings = async (paginationState) => {
    try {
      setIsLoading(true);
      // const { lastEvaluatedKey, limit, isNext } = paginationState;

      const shDetails = await getCarrierMappings(graphClient,
        { CarrierId: carrierId },
        { pagination: { limit: 100 } }
      );

      if (shDetails?.data?.getCarrierShipToMappings) {
        const rawMappings = shDetails.data.getCarrierShipToMappings.Items;

        // Filter out invalid ShipTo items while keeping mappings even without SAPPlantId
        const validMappings = rawMappings.filter((mapping, index) => {
          // Check if ShipTo array has at least one valid item
          const hasValidShipTo = mapping.ShipTo && mapping.ShipTo.some(shipTo =>
            shipTo && shipTo.ShipToNumber && shipTo.ShipToName
          );
          if (!hasValidShipTo) {
            console.warn(`Skipping mapping at index ${index} due to invalid ShipTo data:`, mapping);
            // return false;
          }
          return true;
        }).map(mapping => {
          const sapPlantId = mapping.SK.split('#')[1] || 'N/A';
          return {
            ...mapping,
            // Filter out ShipTo items with null ShipToNumber or ShipToName
            ShipTo: mapping.ShipTo.filter(shipTo =>
              shipTo && shipTo.ShipToNumber && shipTo.ShipToName
            ).map(shipTo => ({
              ...shipTo,
              SAPPlantId: sapPlantId
            }))
          };
        }).filter(mapping => mapping.ShipTo.length > 0); // Remove mappings with no valid ShipTo items

        // Flatten ShipTo array for table display and filter out N/A SAPPlantId
        const flattenedMappings = validMappings.flatMap(mapping =>
          mapping.ShipTo.map(shipTo => ({
            ...shipTo,
            SAPPlantId: shipTo.SAPPlantId,
            ShipTo: [shipTo]
          }))
        ).filter(item => item.SAPPlantId !== 'N/A');
        // Update pagination state
        const newPaginationState = {
          ...paginationState,
          lastEvaluatedKey: shDetails.data.getCarrierShipToMappings.LastEvaluatedKey,
          isNext: !!shDetails.data.getCarrierShipToMappings.LastEvaluatedKey,
          currentPageData: flattenedMappings,
          prevPageData: paginationState.isNext ? pagination.currentPageData : pagination.prevPageData,  // Set prevPageData when moving next
          nextPageData: !paginationState.isNext ? pagination.currentPageData : flattenedMappings,  // Set nextPageData when moving prev
        };

        // Set pagination state
        setPagination(newPaginationState);
        setShipToMappingsAll(flattenedMappings);
      }
    } catch (error) {
      // Show user-friendly error message
      if (error.message?.includes('Cannot return null for non-nullable')) {
        showToastMessage('Some mapping data is incomplete. Displaying valid records only.', 'warning');
      } else {
        handleGraphApiError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextPage = () => {
    const newPagination = {
      ...pagination,
      isPrev: true, // Enable the previous button when moving forward
      isNext: true, // Indicate we're moving to next page
      pageIndex: pagination.pageIndex + 1,
    };
    if (carrierId) {
      fetchAndSetAllShipToMappings(newPagination);
    }
  };

  const handlePreviousPage = () => {
    // Restore previous page data directly without fetching
    const newPagination = {
      ...pagination,
      isNext: true, // Enable the Next button when moving backward
      isPrev: pagination.pageIndex > 1, // Only enable prev if not going to first page
      currentPageData: pagination.prevPageData,
      pageIndex: pagination.pageIndex - 1,
    };
    setPagination(newPagination);
    setShipToMappingsAll(pagination.prevPageData);
  };

  const handlePageSizeChange = (newPageSize) => {
    const newPagination = {
      ...pagination,
      limit: newPageSize,
      pageIndex: 0,
      lastEvaluatedKey: null,
      prevPageData: [],
      isPrev: false,
    };
    setPagination(newPagination);
    if (carrierId) {
      fetchAndSetAllShipToMappings(newPagination);
    }
  };

  useEffect(() => {
    if (carrierId) {
      fetchAndSetAllShipToMappings(pagination);
    }
  }, [carrierId]);

  const handleEdit = (index) => {
    const mapping = shipToMappingsAll[index];
    setSelectedMapping(mapping);
    history.push('/edit-ship-to-mapping', { mapping });
  };

  // const handleEditMapping = (updatedMapping) => {
  //   const newMappings = shipToMappingsAll.map(mapping =>
  //     mapping.ShipToNumber === updatedMapping.ShipToNumber ? updatedMapping : mapping
  //   );
  //   setShipToMappingsAll(newMappings);
  // };

  const handleDelete = (index, SAPPlantId, shipToNumber) => {
    setDeleteShipData({ SAPPlantId, shipToNumber });
    setDeleteIndex(index);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async (carrierId, deleteShipData) => {
    try {
      setIsDeleting(true);
      await deleteCarrierMapping(
        graphClient,
        carrierId,
        deleteShipData.SAPPlantId,
        deleteShipData.shipToNumber,
      )
      const newMappings = shipToMappingsAll.filter((_, i) => i !== deleteIndex);
      setShipToMappingsAll(newMappings);
      setShowConfirmDelete(false);
      setDeleteIndex(null);
      showToastMessage('Ship-to mapping deleted successfully', 'success');
    } catch (error) {
      handleGraphApiError(error);
      showToastMessage(`${error.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // const handleAddMapping = (newMapping) => {
  //   setShipToMappingsAll([newMapping, ...shipToMappingsAll]);
  // };

  return (
    <Container className="ship-to-mapping-container pl-0 pr-0" data-testid="ship-to-mapping-container">
      <NetworkSpeedChecker />

      {/* <PageHeading label="Ship-to Mapping" /> */}
      <Row>
        <Col className="mt-6">
          <h4 className="mb-4">Ship-to Mapping</h4>
        </Col>
      </Row>
      <Row>
        <Col className="mt-6">
          <h6 className="mb-4">Carrier Id: <strong>{carrierId}</strong></h6>
        </Col>
      </Row>
      <Row>
        <Col className="mt-1">
          <h6 className="mb-4">Carrier Name: <strong>{carrierName}</strong></h6>
        </Col>
      </Row>

      <Row className="d-flex">
        <Col sm="3" lg="3" className="px-0 ml-auto">
          <PrimaryButton
            className="submit-button  m-0 p-0"
            data-testid="submit-button"
            type="Submit"
            label="Add Ship-to Mapping"
            onClick={() => history.push('/add-ship-to-mapping', { mapping: {} })}
          />
        </Col>
      </Row>

      <Row data-testid="ship-to-mapping-page" className="pt-4">
        {
          isLoading ? (
            <Col className="text-center">
              <p data-testid="loading-message">Loading ship-to mappings...</p>
              <Spinner id="loading-spinner" className="mx-auto" />
            </Col>
          ) : shipToMappingsAll.length > 0 ? (
            <CommonReactTable
              columns={columns}
              data={shipToMappingsAll}
              pagination={{
                pageIndex: pagination.pageIndex,
                pageSize: pagination.limit,
                onNext: handleNextPage,
                onPrevious: handlePreviousPage,
                onPageSizeChange: handlePageSizeChange,
                canNext: pagination.isNext,
                canPrev: pagination.isPrev,
              }}
            />
          ) : (
            <Col className="text-center">
              <p>No ship-to mappings to show</p>
            </Col>
          )
        }
      </Row>
      {/* <ConfirmActionModal
        isOpen={showConfirmDelete}
        toggle={() => setShowConfirmDelete(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this ship-to mapping?"
      /> */}
      <ConfirmActionModal
        data-testid="lid-warning-window"
        isOpen={showConfirmDelete}
        confirmMsg="Are you sure you want to delete this ship-to mapping?"
        handleCancel={() => { closeWindow(); }}
        handleAction={() => confirmDelete(carrierId, deleteShipData)}
      />
      {/* <AddShipToMappingModal
        graphClient={graphClient}
        isOpen={showAddModal}
        toggle={() => setShowAddModal(false)}
        onAdd={handleAddMapping}
        onSuccess={(message) => showToastMessage(message, 'success')}
      />
      <EditShipToMappingModal
        graphClient={graphClient}
        isOpen={showEditModal}
        toggle={() => setShowEditModal(false)}
        onEdit={handleEditMapping}
        onSuccess={(message) => showToastMessage(message, 'success')}
        initialValues={selectedMapping ? {
          ShipToNumber: selectedMapping.ShipToNumber,
          ShipToName: selectedMapping.ShipToName
        } : {}}
      /> */}
      <div className="toast-container position-fixed top-0 start-50 p-3" style={{ marginTop: '60px' }}>
        <CustomToast
          message={toastMessage}
          type={toastType}
          isOpen={showToast}
          onClose={closeToast}
        />
      </div>
    </Container>
  );
};

/**
 * We use oneOfType because
 * the application uses AppSyncClient
 * the tests/jest provide ApolloClient as we use the <MockProvider/>
 */
ShipToMappingPage.propTypes = {
  graphClient: PropTypes.oneOfType([
    PropTypes.instanceOf(AWSAppSyncClient).isRequired,
    PropTypes.instanceOf(ApolloClient).isRequired,
  ]).isRequired,
};

// withClient will provide the client in props
export default withClient(ShipToMappingPage);
